// Regime Alert Widget — fourth widget on the dashboard's macro section.
//
// Surfaces the AFML meta-classifier's regime-transition probability
// (Step 6.4 of the macro pipeline) as a half-circle gauge plus an
// actionable alert / call-to-action. The gauge uses a Recharts
// ``PieChart`` clipped to its top half (startAngle=180, endAngle=0)
// so a single ``Pie`` series doubles as a progress arc.
//
// Status thresholds (mirrored from the AFML training horizon):
//
//   *  0% – 39%  → Stable      (emerald) — current cycle is holding
//   * 40% – 69%  → Watch       (amber)   — underlying data is shifting
//   * 70%+       → Imminent    (red)     — model has triggered an alert
//
// The colour mapping is deliberately aligned with the existing macro
// regime palette used by ``MacroCompass`` and ``CycleTrajectory`` so
// the user can pattern-match across the four macro widgets without
// a separate legend lookup:
//
//   emerald-500  ─ Expansion / Stable
//   amber-500    ─ Slowdown / Watch
//   red-500      ─ Contraction / Imminent
//
// Iconography is inlined SVG (no lucide-react dependency — the rest of
// the codebase uses ``public/icons.svg`` + raw SVGs the same way).
//
// Empty-state contract: the AFML columns are nullable on
// ``macro_regimes_m`` and the API marks ``afml_prediction`` optional.
// If the prediction is missing the widget renders a soft "warming up"
// card rather than vanishing silently — keeps the dashboard layout
// stable while the worker catches up after a deploy.

import { useEffect, useState, type ReactElement, type SVGProps } from 'react';
import {
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
} from 'recharts';

import {
    dominantRegimeLabel,
    formatMonthEnd,
    macroApi,
    type ForecastWindow,
    type MacroRegimeState,
} from '../../../api/macro';

// ---------------------------------------------------------------------------
// Status palette — keys map to the regime palette so the gauge colour
// is interchangeable with the dot colour on MacroCompass. Each entry
// carries every variant we need for the alert pill (tinted background,
// border, foreground heading) AND the gauge arc (solid hex). Pre-
// declaring full Tailwind class strings keeps the JIT compiler happy
// (it can't see dynamically interpolated class names).
// ---------------------------------------------------------------------------
type StatusKey = 'stable' | 'watch' | 'imminent';

interface StatusVariant {
    /** Solid hex for the gauge arc + headline number. */
    accent: string;
    /** Title shown above the alert body copy. */
    title: string;
    /** Tailwind class chain for the alert callout container. */
    alertClass: string;
    /** Tailwind class chain for the alert title + icon. */
    headingClass: string;
    /** Tailwind class chain for the CTA button — empty for ``stable``
     *  because the button is hidden in that case. */
    ctaClass: string;
    /** SVG icon component rendered next to the alert title. */
    Icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
}

// Inline SVG icons — sized via Tailwind on the consumer.
const CheckCircleIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        {...props}
    >
        <circle cx={12} cy={12} r={10} />
        <path d="m9 12 2 2 4-4" />
    </svg>
);

const InfoIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        {...props}
    >
        <circle cx={12} cy={12} r={10} />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
    </svg>
);

const AlertIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        {...props}
    >
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
    </svg>
);

const STATUS_VARIANTS: Record<StatusKey, StatusVariant> = {
    stable: {
        accent: '#10b981', // emerald-500 — same as Expansion
        title: 'Regime Stable',
        alertClass:
            'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/60',
        headingClass: 'text-emerald-700 dark:text-emerald-300',
        ctaClass: '',
        Icon: CheckCircleIcon,
    },
    watch: {
        accent: '#f59e0b', // amber-500 — same as Slowdown
        title: 'Transition Watch',
        alertClass:
            'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60',
        headingClass: 'text-amber-700 dark:text-amber-300',
        ctaClass:
            'bg-amber-600 hover:bg-amber-700 focus-visible:ring-amber-400 shadow-amber-200/60 dark:shadow-amber-900/40',
        Icon: InfoIcon,
    },
    imminent: {
        accent: '#ef4444', // red-500 — same as Contraction
        title: 'Transition Imminent',
        alertClass:
            'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/60',
        headingClass: 'text-red-700 dark:text-red-300',
        ctaClass:
            'bg-red-600 hover:bg-red-700 focus-visible:ring-red-400 shadow-red-200/60 dark:shadow-red-900/40',
        Icon: AlertIcon,
    },
};

// Soft grey track behind the gauge fill — auto-adjusts to dark mode
// via a CSS custom property so the unfilled portion of the arc
// doesn't glow on a dark card.
const GAUGE_TRACK = 'var(--regime-gauge-track, #e2e8f0)';

function classifyTransitionRisk(probPct: number): StatusKey {
    if (probPct >= 70) return 'imminent';
    if (probPct >= 40) return 'watch';
    return 'stable';
}

function buildMessage(
    status: StatusKey,
    regimeLabel: string,
    horizonMonths: number,
): string {
    if (status === 'imminent') {
        return (
            `High probability of exiting the ${regimeLabel} cycle within ` +
            `the next ${horizonMonths} months. Defensive portfolio ` +
            `adjustments are recommended.`
        );
    }
    if (status === 'watch') {
        return (
            `Underlying indicators are shifting. The ${regimeLabel} cycle ` +
            `may be nearing its end — monitor closely and review your ` +
            `tilted allocation.`
        );
    }
    return (
        `The current ${regimeLabel} cycle shows strong stability. Your ` +
        `baseline allocation is well-positioned for the next ` +
        `${horizonMonths} months.`
    );
}

/**
 * Collapse the backend forecast window into a compact human label.
 *
 * ``{ start_label: "Jul 2026", end_label: "Sep 2026" }`` -> ``"Jul–Sep
 * 2026"`` when both months share a year, otherwise the years are kept
 * on both sides (``"Nov 2026 – Jan 2027"``). Returns ``null`` when the
 * window is missing so the caller can fall back to horizon-only copy.
 */
function buildWindowLabel(window: ForecastWindow | null | undefined): string | null {
    if (!window?.start_label || !window?.end_label) return null;
    const [startMonth, startYear] = window.start_label.split(' ');
    const [endMonth, endYear] = window.end_label.split(' ');
    if (startYear && startYear === endYear) {
        return `${startMonth}–${endMonth} ${endYear}`;
    }
    return `${window.start_label} – ${window.end_label}`;
}

interface RegimeAlertWidgetProps {
    /**
     * Optional callback fired when the user clicks the
     * "Review Tilted Allocation" CTA. Typically wired to a smooth
     * scroll into the RegimeTilt section (see ``forge-ultra-dashboard``).
     */
    onReviewPortfolio?: () => void;
}

export const RegimeAlertWidget = ({
    onReviewPortfolio,
}: RegimeAlertWidgetProps) => {
    const [state, setState] = useState<MacroRegimeState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await macroApi.getCurrentRegime();
                if (!cancelled) setState(result);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error ? err.message : 'Failed to load.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <div className="surface-card-skeleton h-80" aria-busy>
                <p className="muted text-sm">Loading transition forecast…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="surface-card-error">
                <p className="font-bold">Regime Alert unavailable</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    const prediction = state?.afml_prediction ?? null;
    const transitionProb = prediction?.transition_probability ?? null;

    if (!state || transitionProb === null) {
        // Soft empty-state — keeps the dashboard layout stable while
        // the AFML worker is producing its first prediction after a
        // deploy. Matches the tone of MacroCompass's empty state.
        return (
            <div className="surface-card">
                <h3 className="section-title">Macro Regime Transition Risk</h3>
                <p className="muted text-sm mt-2">
                    The transition-probability model is warming up. The
                    forecast will appear here once the AFML worker has
                    produced its first prediction (typically within a
                    few minutes after a fresh deploy).
                </p>
            </div>
        );
    }

    // The backend stores ``transition_probability`` as a fraction in
    // [0, 1]; clamp defensively in case a future model emits values
    // slightly outside that range.
    const probPct = Math.max(0, Math.min(100, transitionProb * 100));
    const status = classifyTransitionRisk(probPct);
    const variant = STATUS_VARIANTS[status];
    const StatusIcon = variant.Icon;
    // Derive the live regime from the ensemble probabilities (argmax) so
    // this card can NEVER drift from the Macro Compass headline or the
    // Tilted Allocation card — all three now key off the same dominant
    // regime rather than the sticky, rule-based ``regime_label`` that
    // used to lag the live cycle. The backend ``/current-regime`` already
    // returns the effective label; recomputing it client-side from the
    // probabilities is a cheap belt-and-suspenders guarantee that the
    // forecast card stays dynamic even if a future backend regresses.
    const regimeLabel =
        dominantRegimeLabel(state) || state.regime_label || 'current';
    const horizon = prediction?.horizon_months ?? 3;
    const message = buildMessage(status, regimeLabel, horizon);
    // Explicit AFML forecast window ("Jul–Sep 2026") derived server-side
    // from the regime month-end + horizon, so the copy below states the
    // exact months the transition risk covers rather than a bare horizon.
    const windowLabel = buildWindowLabel(state.forecast_window);

    // Two-slice pie that only fills the top half of the chart area.
    // The trailing zero-value slice is omitted when prob === 100 so
    // Recharts doesn't draw a hairline artefact at the right edge.
    const gaugeData = [
        { name: 'risk', value: probPct },
        { name: 'remaining', value: Math.max(0, 100 - probPct) },
    ];

    return (
        <div
            className="surface-card flex flex-col"
            style={{ borderColor: variant.accent }}
        >
            <div className="flex items-baseline justify-between gap-3">
                <div>
                    <h3 className="section-title">Cycle Stability Forecast</h3>
                    <p className="muted text-xs">
                        Transition risk within next {horizon} months
                        {windowLabel ? ` (${windowLabel})` : ''}
                    </p>
                    <p className="muted text-[11px]">
                        As of · {formatMonthEnd(state.date)}
                    </p>
                </div>
                <div className="text-right">
                    <p
                        className="text-2xl font-black"
                        style={{ color: variant.accent }}
                    >
                        {variant.title}
                    </p>
                    <p className="muted text-xs">
                        {regimeLabel} cycle
                    </p>
                </div>
            </div>

            {/* Half-circle gauge.
                We render the PieChart inside a fixed-height box and
                rely on Recharts' polar projection (startAngle=180,
                endAngle=0) to clip it to the top half. The numeric
                readout is absolutely positioned over the bottom of the
                same box so the layout stays tight regardless of the
                surrounding card width. */}
            <div
                className="relative mt-4 w-full"
                style={{ height: 168 }}
                role="img"
                aria-label={`Transition risk: ${probPct.toFixed(1)} percent`}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={gaugeData}
                            cx="50%"
                            cy="100%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={86}
                            outerRadius={120}
                            paddingAngle={0}
                            dataKey="value"
                            stroke="none"
                            isAnimationActive
                            animationDuration={500}
                        >
                            <Cell fill={variant.accent} />
                            <Cell fill={GAUGE_TRACK} />
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>

                <div className="absolute inset-x-0 bottom-0 flex flex-col items-center">
                    <span
                        className="text-4xl font-black tabular-nums leading-none"
                        style={{ color: variant.accent }}
                    >
                        {probPct.toFixed(1)}%
                    </span>
                    <span className="muted text-[11px] uppercase tracking-wider font-semibold mt-1">
                        Transition Risk
                    </span>
                </div>
            </div>

            {/* Threshold legend — keeps the three bands visible at a
                glance so the headline number has obvious context. */}
            <ul className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
                {([
                    { key: 'stable', label: '0–40%', sub: 'Stable' },
                    { key: 'watch', label: '40–70%', sub: 'Watch' },
                    { key: 'imminent', label: '70%+', sub: 'Imminent' },
                ] as { key: StatusKey; label: string; sub: string }[]).map(
                    (band) => {
                        const active = band.key === status;
                        const bandAccent = STATUS_VARIANTS[band.key].accent;
                        return (
                            <li
                                key={band.key}
                                className={
                                    'rounded-lg px-2 py-1.5 text-center transition-colors ' +
                                    (active
                                        ? 'bg-slate-100 dark:bg-slate-900/60 font-bold'
                                        : 'opacity-60')
                                }
                                style={
                                    active
                                        ? { color: bandAccent }
                                        : undefined
                                }
                            >
                                <span className="block tabular-nums">
                                    {band.label}
                                </span>
                                <span className="block uppercase tracking-wider">
                                    {band.sub}
                                </span>
                            </li>
                        );
                    },
                )}
            </ul>

            {/* Actionable alert block. */}
            <div
                className={
                    'mt-5 w-full p-4 rounded-xl flex items-start gap-3 ' +
                    variant.alertClass
                }
            >
                <StatusIcon
                    className={'w-6 h-6 flex-shrink-0 mt-0.5 ' + variant.headingClass}
                />
                <div className="flex-1 min-w-0">
                    <h4 className={'font-bold ' + variant.headingClass}>
                        {variant.title}
                    </h4>
                    <p className="text-sm mt-1 leading-relaxed text-slate-700 dark:text-slate-300">
                        {message}
                    </p>
                </div>
            </div>

            {/* CTA — always rendered when a callback is wired in so the
                user can jump straight to the cycle-driven tilted
                allocation regardless of current transition risk. The
                button colour matches the active status variant for
                ``watch`` / ``imminent``; ``stable`` falls back to the
                emerald accent so it still reads as the primary CTA. */}
            {onReviewPortfolio && (
                <button
                    type="button"
                    onClick={onReviewPortfolio}
                    className={
                        'mt-5 w-full py-3 px-4 rounded-xl font-bold text-white ' +
                        'transition-all shadow-md hover:shadow-lg ' +
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
                        'focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 ' +
                        (variant.ctaClass ||
                            'bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-400 shadow-emerald-200/60 dark:shadow-emerald-900/40')
                    }
                >
                    Review Tilted Allocation
                </button>
            )}
        </div>
    );
};

export default RegimeAlertWidget;
